/*
 * AppDrawer — 项目统一 Drawer 封装（design-spec §10.2）
 * 规范：三档宽度 / 右侧出现 / closable / destroyOnClose / 底部固定 footer（左取消右主操作）
 */
import React from 'react';
import { Drawer, Button, Space } from 'antd';
import type { DrawerProps, ButtonProps } from 'antd';

export type AppDrawerSize = 'sm' | 'md' | 'lg';

const SIZE_WIDTH: Record<AppDrawerSize, number> = {
  sm: 480,
  md: 720,
  lg: 960,
};

export interface AppDrawerProps extends Omit<DrawerProps, 'width' | 'footer' | 'size'> {
  /** 三档宽度：sm 480 / md 720 / lg 960 */
  size?: AppDrawerSize;
  /** 自定义 width 时不走 size（特殊场景） */
  width?: number;
  /** 取消按钮文案，默认「取消」 */
  cancelText?: string;
  /** 主操作按钮文案，默认「确定」 */
  okText?: string;
  /** 主操作点击 */
  onOk?: () => void | Promise<void>;
  /** 提交 loading（控制主按钮 loading） */
  confirmLoading?: boolean;
  /** 主按钮是否危险态 */
  danger?: boolean;
  /** 不显示底部按钮组（纯展示型） */
  hideFooter?: boolean;
  /** 完全自定义 footer（覆盖默认按钮组） */
  footer?: React.ReactNode;
  /** 主按钮其它 props */
  okButtonProps?: ButtonProps;
  /** 取消按钮其它 props */
  cancelButtonProps?: ButtonProps;
}

const FOOTER_WRAP_STYLE: React.CSSProperties = {
  display: 'flex',
  justifyContent: 'flex-end',
  padding: '12px 24px',
  borderTop: '1px solid rgba(0,0,0,0.06)',
  background: '#fff',
};

const AppDrawer: React.FC<AppDrawerProps> = ({
  size = 'md',
  width,
  cancelText = '取消',
  okText = '确定',
  onOk,
  onClose,
  confirmLoading,
  danger,
  hideFooter,
  footer,
  okButtonProps,
  cancelButtonProps,
  placement = 'right',
  closable = true,
  destroyOnClose = true,
  children,
  ...rest
}) => {
  const resolvedFooter = hideFooter
    ? null
    : footer !== undefined
      ? footer
      : (
        <div style={FOOTER_WRAP_STYLE}>
          <Space>
            <Button onClick={onClose as any} {...cancelButtonProps}>{cancelText}</Button>
            <Button
              type="primary"
              danger={danger}
              loading={confirmLoading}
              onClick={() => onOk?.()}
              {...okButtonProps}
            >
              {okText}
            </Button>
          </Space>
        </div>
      );

  // 兼容 antd v5 (bodyStyle / destroyOnClose) 与 v6 (styles.body / destroyOnHidden)
  const compatProps: any = {
    destroyOnClose,
    destroyOnHidden: destroyOnClose,
    bodyStyle: { paddingBottom: 24 },
    styles: { body: { paddingBottom: 24 } },
  };
  return (
    <Drawer
      width={width ?? SIZE_WIDTH[size]}
      placement={placement}
      closable={closable}
      onClose={onClose}
      footer={resolvedFooter}
      {...compatProps}
      {...rest}
    >
      {children}
    </Drawer>
  );
};

export default AppDrawer;
